import React, { Component } from 'react';
import SimpleHeader from 'components/shared/table/SimpleHeader';
import SimpleTable from 'components/shared/table/SimpleTable';
import PropTypes from 'prop-types';
import './QuotesTable.css';
import Number from 'components/shared/text/Number';
import roundEmptyPng from 'images/round-empty.png';
import roundFullPng from 'images/round-full.png';
import QuoteStatus from 'components/shared/table/cell-addons/QuoteStatus';
import numeral from 'numeral';

/**
 * Map quotes from the same task to a single task object with an array of these quotes.
 * @param quotes
 * @param budgetTasksByTaskId
 */
export function mapQuotes(quotes, budgetTasksByTaskId = null) {
  const result = {};

  quotes.forEach((quote) => {
    let task = result[quote.task.id];
    if (task === undefined) {
      task = {
        id: quote.task.id,
        number: quote.task.number,
        trade: {
          id: quote.task.trade.id,
          value: quote.task.trade.value,
          label: quote.task.trade.label,
        },
        quotes: [],
      };

      result[task.id] = task;
    }

    let data = {
      id: quote.id,
      status: quote.status,
      counterStatus: quote.counter_status,
      estimate: quote.estimate,
      counter: quote.counter,
      value: quote.value,
      contractor: {
        id: quote.contractor_id,
        name: quote.contractor_name,
      },
    };
    if (
      budgetTasksByTaskId !== null &&
      budgetTasksByTaskId[quote.task.id] !== undefined
    ) {
      const budgetTask = budgetTasksByTaskId[quote.task.id];
      const totalCost =
        numeral(budgetTask.cost).value() +
        numeral(budgetTask.fee).value() +
        numeral(budgetTask.markup).value() +
        numeral(budgetTask.insurance).value();
      data = {
        ...data,
        budget: totalCost,
      };
    }
    task.quotes.push(data);
  });

  return result;
}

class QuotesTable extends Component {
  static propTypes = {
    budgetTasksByTaskId: PropTypes.object,
    quoteTableRadios: PropTypes.object,
    quotes: PropTypes.array,
    changeOrderTasks: PropTypes.array,
    proposalSelectable: PropTypes.bool,
    changeOrderSelectable: PropTypes.bool,
    selectedFilter: PropTypes.string,
    onFilterSelect: PropTypes.func,
    onRadioSelect: PropTypes.func,
    onRowSelected: PropTypes.func, // returns quote id
  };

  static defaultProps = {
    budgetTasksByTaskId: {},
  };

  handleRadioSelect = (taskId, quoteId, event) => {
    event.preventDefault();
    event.stopPropagation(); // To prevent to trigger row click event
    this.props.onRadioSelect(taskId, quoteId);
  };

  handleFilterSelect = (newFilter) => {
    if (this.props.selectedFilter !== newFilter) {
      this.props.onFilterSelect(newFilter);
    }
  };

  handleRowSelect = (quoteId) => {
    if (quoteId[0] === '_') {
      this.props.onChangeOrderRowSelected(quoteId.substr(1));
    } else {
      this.props.onRowSelected(quoteId);
    }
  };

  render() {
    const showBudgetTaskCost =
      Object.keys(this.props.budgetTasksByTaskId).length > 0;
    if (showBudgetTaskCost) {
      this.rows = this.createRows(
        mapQuotes(this.props.quotes, this.props.budgetTasksByTaskId),
        showBudgetTaskCost
      );
    } else {
      this.rows = this.createRows(mapQuotes(this.props.quotes));
    }

    const headers = [
      'TASK #',
      'TRADE',
      'CO #',
      <SimpleHeader
        label="STATUS"
        filter={{
          options: [
            {
              id: 'all',
              label: 'All',
            },
            {
              id: 'waiting',
              label: 'Waiting',
            },
            {
              id: 'received',
              label: 'Received',
            },
            {
              id: 'denied',
              label: 'Denied',
            },
          ],
          selected: this.props.selectedFilter,
        }}
        onFilterOptionSelect={this.handleFilterSelect}
      />,
    ];

    if (showBudgetTaskCost) {
      headers.push('ESTIMATE COST');
      headers.push('CONTRACTOR ESTIMATE');
    } else {
      headers.push('ESTIMATE');
    }
    headers.push(''); // SELECT radio button

    return (
      <div className="quotes-table-container">
        <SimpleTable
          className={`quotes-table${showBudgetTaskCost ? ' budget' : ''}`}
          headers={headers}
          rows={this.rows}
          emptyMessage="No Quotes"
          onRowSelected={this.handleRowSelect}
        />
      </div>
    );
  }

  createRows(tasksWithQuotes, showBudgetTaskCost = false) {
    const rows = [];

    Object.keys(tasksWithQuotes).forEach((key) => {
      const task = tasksWithQuotes[key];
      const changeOrderTasks = this.props.changeOrderTasks.filter(
        (cot) => cot.task_id === task.id && cot.change_order_quote
      );
      let awardedContractor = null;

      // If current task has a selected quote
      const selectedQuote = this.props.quoteTableRadios[task.id];

      rows.push({
        id: `t${task.id}`, // prefix with t to avoid same id with quote
        selectable: false,
        data: [
          <span className="h2">Task # {task.number}</span>,
          '',
          '',
          '',
          '',
          '',
        ].concat(showBudgetTaskCost ? [''] : null),
      });

      task.quotes.forEach((quote) => {
        if (quote.status === 'approved') {
          awardedContractor = quote.contractor;
        }

        let data = [
          quote.contractor.name,
          task.trade.label,
          '',
          <QuoteStatus
            status={
              quote.status === 'received' && quote.counterStatus !== 'none'
                ? quote.counterStatus
                : quote.status
            }
          />,
        ];
        if (quote.hasOwnProperty('budget')) {
          data.push(<Number value={quote.budget} format="$0,0" />);
        } else if (showBudgetTaskCost) {
          data.push('');
        }
        data = data.concat([
          quote.estimate !== null ? (
            <Number
              value={
                quote.status === 'received' &&
                quote.counterStatus === 'negotiating'
                  ? numeral(quote.counter).value() +
                    numeral(quote.value).value()
                  : numeral(quote.estimate).value() +
                    numeral(quote.value).value()
              }
              format="$0,0"
            />
          ) : (
            'N/A'
          ),
          quote.status === 'received' &&
          this.props.proposalSelectable &&
          this.props.onRadioSelect !== undefined ? (
            <img
              src={selectedQuote === quote.id ? roundFullPng : roundEmptyPng}
              alt={selectedQuote === quote.id ? 'selected' : 'not-selected'}
              onClick={this.handleRadioSelect.bind(this, task.id, quote.id)}
            />
          ) : (
            ''
          ),
        ]);
        rows.push({ id: quote.id, data });
      });

      let self = this;
      changeOrderTasks
        .sort((cot1, cot2) => cot1.number - cot2.number)
        .forEach(function (changeOrderTask) {
          const changeOrderQuote = changeOrderTask.change_order_quote;
          let data = [
            awardedContractor.name,
            task.trade.label,
            <Number value={changeOrderTask.number} format="00" />,
            <QuoteStatus status={changeOrderQuote.status} />,
          ];
          data = data.concat([
            changeOrderQuote.estimate !== null ? (
              <Number
                value={
                  numeral(changeOrderQuote.estimate).value() +
                  numeral(changeOrderQuote.value).value()
                }
                format="$0,0"
              />
            ) : (
              'N/A'
            ),
            changeOrderQuote.status === 'received' &&
            self.props.changeOrderSelectable &&
            self.props.onRadioSelect !== undefined ? (
              <img
                src={
                  selectedQuote === changeOrderTask.id
                    ? roundFullPng
                    : roundEmptyPng
                }
                alt={
                  selectedQuote === changeOrderTask.id
                    ? 'selected'
                    : 'not-selected'
                }
                onClick={self.handleRadioSelect.bind(
                  self,
                  task.id,
                  changeOrderTask.id
                )}
              />
            ) : (
              ''
            ),
          ]);
          rows.push({ id: `_${changeOrderTask.id}`, data });
        });
    });

    return rows;
  }
}

export default QuotesTable;



// WEBPACK FOOTER //
// ./src/components/admin/work-orders/details/quotes/QuotesTable.js